import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    X, 
    Folder, 
    Trash2, 
    Clock, 
    Edit2, 
    Check, 
    Loader2,
    Cloud,
    CloudOff
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SavedBlueprintsModal = ({ isOpen, onClose, onSelectBlueprint }) => {
    const { isAuthenticated, token, getAuthHeaders } = useAuth();
    const [blueprints, setBlueprints] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState('');
    const [deletingId, setDeletingId] = useState(null);

    const fetchBlueprints = useCallback(async () => {
        if (!isAuthenticated || !token) {
            setIsLoading(false);
            return;
        }
        
        setIsLoading(true);
        try {
            const headers = getAuthHeaders();
            const response = await axios.get(`${BACKEND_URL}/api/blueprints`, { headers });
            setBlueprints(response.data);
        } catch (error) {
            console.error('Failed to fetch blueprints:', error);
            toast.error('Failed to load saved blueprints');
        } finally {
            setIsLoading(false);
        }
    }, [isAuthenticated, token, getAuthHeaders]);

    useEffect(() => {
        if (isOpen && isAuthenticated && token) {
            fetchBlueprints();
        }
    }, [isOpen, isAuthenticated, token, fetchBlueprints]);

    const handleSelect = (blueprint) => {
        onSelectBlueprint(blueprint.idea, blueprint.blueprint);
        onClose();
    };

    const handleDelete = async (id) => {
        setDeletingId(id);
        try {
            await axios.delete(`${BACKEND_URL}/api/blueprints/${id}`, {
                headers: getAuthHeaders()
            });
            setBlueprints(prev => prev.filter(b => b.id !== id));
            toast.success('Blueprint deleted');
        } catch (error) {
            toast.error('Failed to delete blueprint');
        } finally {
            setDeletingId(null);
        }
    };

    const handleStartEdit = (blueprint) => {
        setEditingId(blueprint.id);
        setEditName(blueprint.name);
    };

    const handleSaveEdit = async (id) => {
        if (!editName.trim()) {
            toast.error('Name cannot be empty');
            return;
        }

        try {
            await axios.put(`${BACKEND_URL}/api/blueprints/${id}`, 
                { name: editName.trim() },
                { headers: getAuthHeaders() }
            );
            setBlueprints(prev => prev.map(b => 
                b.id === id ? { ...b, name: editName.trim() } : b
            ));
            setEditingId(null);
            toast.success('Blueprint renamed');
        } catch (error) {
            toast.error('Failed to rename blueprint');
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = now - date;
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        
        if (days === 0) return 'Today';
        if (days === 1) return 'Yesterday';
        if (days < 7) return `${days} days ago`;
        return date.toLocaleDateString();
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="glass-card w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col relative z-10"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="saved-blueprints-modal"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-5 border-b border-white/10">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-primary/20">
                                <Cloud className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <h3 className="font-heading font-semibold text-lg">Saved Blueprints</h3>
                                <p className="text-xs text-gray-500">
                                    {blueprints.length} blueprint{blueprints.length !== 1 ? 's' : ''} saved to your account
                                </p>
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onClose}
                            className="hover:bg-white/10"
                        >
                            <X className="w-5 h-5" />
                        </Button>
                    </div>

                    {/* Content */}
                    <ScrollArea className="flex-1">
                        <div className="p-5">
                            {!isAuthenticated ? (
                                <div className="text-center py-12">
                                    <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                                        <CloudOff className="w-8 h-8 text-gray-500" />
                                    </div>
                                    <p className="text-gray-400 mb-2">Sign in to save blueprints</p>
                                    <p className="text-sm text-gray-600">
                                        Create an account to save and access your blueprints from anywhere
                                    </p>
                                </div>
                            ) : isLoading ? (
                                <div className="text-center py-12">
                                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
                                    <p className="text-gray-500">Loading your blueprints...</p>
                                </div>
                            ) : blueprints.length === 0 ? (
                                <div className="text-center py-12">
                                    <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                                        <Folder className="w-8 h-8 text-gray-500" />
                                    </div>
                                    <p className="text-gray-400 mb-2">No saved blueprints yet</p>
                                    <p className="text-sm text-gray-600">
                                        Generate a blueprint and click "Save to Account" to store it
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {blueprints.map((blueprint) => (
                                        <motion.div
                                            key={blueprint.id}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="p-4 bg-surface rounded-lg border border-white/5 hover:border-primary/20 transition-all group"
                                        >
                                            <div className="flex items-start justify-between gap-4">
                                                <div 
                                                    className="flex-1 cursor-pointer"
                                                    onClick={() => handleSelect(blueprint)}
                                                >
                                                    {editingId === blueprint.id ? (
                                                        <div className="flex items-center gap-2">
                                                            <Input
                                                                value={editName}
                                                                onChange={(e) => setEditName(e.target.value)}
                                                                className="h-8 bg-background border-primary/50"
                                                                autoFocus
                                                                onClick={(e) => e.stopPropagation()}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') handleSaveEdit(blueprint.id);
                                                                    if (e.key === 'Escape') setEditingId(null);
                                                                }}
                                                            />
                                                            <Button
                                                                size="icon"
                                                                variant="ghost"
                                                                className="h-8 w-8 hover:bg-success/20"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleSaveEdit(blueprint.id);
                                                                }}
                                                            >
                                                                <Check className="w-4 h-4 text-success" />
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <h4 className="font-medium text-white group-hover:text-primary transition-colors">
                                                            {blueprint.name}
                                                        </h4>
                                                    )}
                                                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                                        {blueprint.idea}
                                                    </p>
                                                    <div className="flex items-center gap-1 mt-2 text-xs text-gray-600">
                                                        <Clock className="w-3 h-3" />
                                                        {formatDate(blueprint.created_at)}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        className="h-8 w-8 hover:bg-white/10"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleStartEdit(blueprint);
                                                        }}
                                                        data-testid={`rename-blueprint-${blueprint.id}`}
                                                    >
                                                        <Edit2 className="w-4 h-4 text-gray-400" />
                                                    </Button>
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        className="h-8 w-8 hover:bg-destructive/20"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete(blueprint.id);
                                                        }}
                                                        disabled={deletingId === blueprint.id}
                                                        data-testid={`delete-blueprint-${blueprint.id}`}
                                                    >
                                                        {deletingId === blueprint.id ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <Trash2 className="w-4 h-4 text-destructive" />
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </ScrollArea>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};
